import { Popover } from 'antd';
import { m } from 'framer-motion';
import Image from 'next/image';
import { useCallback, useMemo, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import useTranslation from 'next-translate/useTranslation';
import arrowDownSelect from '@/components/ielts/listening/assets/images/arrow-down-select.png';
import arrowDownSelectWhite from '@/components/ielts/listening/assets/images/arrow-down-select-white.png';
import IeltsInputResultIcon from '../ResultIcon';
import useIeltsTheme from '../../hooks/useIeltsTheme';

type Option = {
  label: React.ReactNode;
  value: string;
  title?: React.ReactNode;
  id?: string;
};

type Props = {
  index?: number;
  options: Option[];
  value: string;
  className?: string;
  onSelect?: (value: string, option?: Option) => void;
  readonly?: boolean;
  id?: string;
  result?: boolean;
  showIcon?: boolean;
};

function IeltsSelect({ id, result, index, className = '', options, onSelect, value, readonly, showIcon = true }: Props) {
  const { t } = useTranslation('ielts');
  const { theme } = useIeltsTheme();
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find((option) => option.value === value);
  // Two fixed PNGs, so this cannot resolve through a CSS variable. `fg` is white
  // in exactly the theme whose control ground is dark, which is the condition
  // the white glyph actually depends on.
  const arrowIcon = theme.fg === '#FFFFFF' ? arrowDownSelectWhite : arrowDownSelect;

  const [open, setOpen] = useState(false);

  const handleClick = useCallback(
    (o: Option) => {
      onSelect?.(o.value, o);
      setOpen(false);
    },
    [onSelect]
  );

  const content = useMemo(() => {
    return (
      <div
        className="w-[218px] border rounded-[10px] py-2"
        style={{
          borderColor: theme.inputBorderColor,
        }}
      >
        {options.map((option) => (
          <m.div key={option.value} className={twMerge('px-8 py-2 cursor-pointer', 'hover:bg-gray-200')} onClick={() => handleClick(option)}>
            <span className="text-[#212B36] text-sm">{option.title}</span>
          </m.div>
        ))}
      </div>
    );
  }, [handleClick, options, theme.inputBorderColor]);

  return (
    <div id={id} ref={containerRef} className={twMerge('inline-flex gap-x-[11px] items-center w-fit relative', className)}>
      {typeof index === 'number' && (
        <div
          className="w-[26px] h-[26px] rounded-full flex justify-center items-center select-none"
          style={{
            backgroundColor: theme.questionIndexBgColor,
            boxShadow: `0px 2px 0px 0px ${theme.questionIndexShadowColor} inset`,
          }}
        >
          <span className="text-[#383838] text-[13px] font-bold leading-[26px]">{index}</span>
        </div>
      )}
      <Popover
        content={content}
        overlayInnerStyle={{ padding: 0, borderRadius: 10 }}
        arrow={false}
        trigger={readonly ? undefined : 'click'}
        open={readonly ? false : open}
        onOpenChange={readonly ? undefined : setOpen}
        getPopupContainer={() => containerRef.current || document.body}
      >
        <div
          className={twMerge(
            'min-w-[150px] h-[36px] border-[1px] border-solid  overflow-hidden px-3 flex items-center justify-between cursor-pointer',
            'bg-ielts-control text-ielts-fg rounded-[var(--ielts-control-radius)]',
          )}
          style={{ borderColor: theme.inputBorderColor }}
        >
          {selectedOption ? (
            <span className="text-base font-bold flex-1 text-ielts-fg">{selectedOption?.title}</span>
          ) : readonly ? (
            <span className="text-gray-400 text-sm italic font-bold">{t('listening_reading.no_answer')}</span>
          ) : (
            <span />
          )}
          <Image src={arrowIcon} alt="down" className={twMerge('w-6 h-6', showIcon ? '' : 'invisible')} />
        </div>
      </Popover>

      <IeltsInputResultIcon className="absolute -top-[6px] -right-1" result={result} />

      {/* {selectedOption && <p>{selectedOption.title}</p>} */}
    </div>
  );
}

export default IeltsSelect;
